/**
 * Calculs purs du tableau de bord d'un projet (`views/DashboardView.jsx`), à partir de la liste
 * complète des tâches. Les phases (`kind: 'phase'`) n'entrent jamais dans les compteurs : leurs
 * champs sont des cumuls de leurs enfants (même règle que `stats.service.js`).
 */

import { DONE_STATUSES } from '@exiplan/shared-types';
import { addDays, daysBetween } from './calendar.js';

const isOpen = (task) => !DONE_STATUSES.includes(task.status);
const isWork = (task) => task.kind !== 'phase';

/** Date de référence d'un jalon : son échéance, à défaut son début. */
const milestoneDate = (task) => task.dueDate ?? task.startDate ?? null;

/** Tâches en retard (échéance dépassée, non terminées), de la plus en retard à la moins. */
export function overdueTasks(tasks, today) {
  return tasks
    .filter((task) => isWork(task) && isOpen(task) && task.dueDate && task.dueDate < today)
    .map((task) => ({ task, daysLate: daysBetween(task.dueDate, today) }))
    .sort((a, b) => b.daysLate - a.daysLate || a.task.number - b.task.number);
}

/** Échéances de tâches (hors jalons) de `today` inclus à `today + days` inclus, par date. */
export function upcomingDeadlines(tasks, today, days = 7) {
  const limit = addDays(today, days);
  return tasks
    .filter(
      (task) =>
        isWork(task) &&
        task.kind !== 'milestone' &&
        isOpen(task) &&
        task.dueDate &&
        task.dueDate >= today &&
        task.dueDate <= limit,
    )
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.number - b.number);
}

/** Jalons non atteints dont la date n'est pas passée, du plus proche au plus lointain. */
export function upcomingMilestones(tasks, today) {
  return tasks
    .filter((task) => task.kind === 'milestone' && isOpen(task) && milestoneDate(task) >= today)
    .sort((a, b) => milestoneDate(a).localeCompare(milestoneDate(b)) || a.number - b.number);
}

/** Lots (`kind: 'phase'`) dans l'ordre de leur numéro. */
export function phases(tasks) {
  return tasks.filter((task) => task.kind === 'phase').sort((a, b) => a.number - b.number);
}

/** Répartition des tâches (hors phases) par responsable : `[{ key, name, count }]`, décroissant. */
export function byAssignee(tasks) {
  const groups = new Map();
  for (const task of tasks.filter(isWork)) {
    const key = task.assignee?.id ?? task.assigneeId ?? null;
    const entry = groups.get(key) ?? { key, name: task.assignee?.name ?? null, count: 0 };
    entry.count += 1;
    groups.set(key, entry);
  }
  return [...groups.values()].sort((a, b) => b.count - a.count);
}
